import * as React from 'react';
import { Alert, Col, Row } from 'reactstrap';
import { showAuditLog } from '../../app-framework/AuditLogDlg';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { CostcoCompleteLeadDlg } from '../../dialogs/CostcoCompleteLeadDlg';
import { addEntity, loadEntityNavigationPropertyValue, queryNoTracking, setEntityMemberValue, update } from '../../services/DataService';
import { accentUtils, formHelper, showDialog, showOK } from '../../services/HelperService';
import { opporunityStatus } from '../../services/JobService';
import { getAccess, isCostcoIntegrationEnabled } from '../../services/UserService';
import { AccentButton } from '../AccentButton';
import { AccentSectionHeader } from '../AccentSectionHeader';
import { AccentSpinner } from '../AccentSpinner';
import { AccentFormCtrl, AccentFormCtrlNoViewState } from '../bound/AccentFormCtrl';
import { AccentComboUnbound } from '../unbound/AccentComboUnbound';
import { AccentDateUnbound } from '../unbound/AccentDateUnbound';
import { AccentNumberUnbound } from '../unbound/AccentNumberUnbound';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';

const query_CostcoWarehouse_SelectList = queryNoTracking("CostcoWarehouse_SelectList");
const query_CostcoService_SelectList = queryNoTracking("CostcoService_SelectList");
const query_CostcoAdCode_SelectList = queryNoTracking("CostcoAdCode_SelectList");
const query_CostcoCashCard_SelectList = queryNoTracking("CostcoCashCard_SelectList");
const query_CostcoPaymentMethod_SelectList = queryNoTracking("CostcoPaymentMethod_SelectList");
const query_CostcoCancelReason_SelectList = queryNoTracking("CostcoCancelReason_SelectList");
const query_Costco_BusinessUnitMapping = queryNoTracking("Costco_BusinessUnitMapping");
const query_CostcoLead_ContactMembershipNumber = queryNoTracking("CostcoLead_ContactMembershipNumber");

// Editable Costco lead input fields shared between the opportunity/job view and
// the new lead wizard. The lead can be an entity or a plain setProperty object.
// onChange (optional) fires when the warehouse or service selection changes.
export const CostcoLeadFieldsCtrl = ({ lead, readOnly, noViewState = false, includeLeadGenerator = false, onChange }) => {

    const FormCtrl = noViewState ? AccentFormCtrlNoViewState : AccentFormCtrl;

    const getWarehouseQuery = () => query_CostcoWarehouse_SelectList.getAllAsQueryableWithCache({ includeID: lead.CostcoWarehouseID });
    const getServiceQuery = () => query_CostcoService_SelectList.getAllAsQueryableWithCache({ includeID: lead.CostcoServiceID });
    const getAdCodeQuery = () => query_CostcoAdCode_SelectList.getAllAsQueryableWithCache({ includeID: lead.CostcoAdCodeID });
    const getCashCardQuery = () => query_CostcoCashCard_SelectList.getAllAsQueryableWithCache({ includeID: lead.CostcoCashCardID });
    const getPaymentMethodQuery = () => query_CostcoPaymentMethod_SelectList.getAllAsQueryableWithCache({ includeID: lead.CostcoPaymentMethodID });

    const warehouse = formHelper.getFieldCtrlModel(lead, "CostcoWarehouseID", "select", true, { label: "Warehouse", displayField: "DisplayName", source: getWarehouseQuery, sourceType: "CostcoWarehouse", readOnly, defaultSingle: true, onChange });
    const service = formHelper.getFieldCtrlModel(lead, "CostcoServiceID", "select", true, { label: "Service", displayField: "DisplayName", source: getServiceQuery, sourceType: "CostcoService", readOnly, defaultSingle: true, onChange });
    const adCode = formHelper.getFieldCtrlModel(lead, "CostcoAdCodeID", "select", false, { label: "Ad Code", displayField: "DisplayName", source: getAdCodeQuery, sourceType: "CostcoAdCode", readOnly });
    const cashCard = formHelper.getFieldCtrlModel(lead, "CostcoCashCardID", "select", false, { label: "Cash Card Promotion", displayField: "DisplayName", source: getCashCardQuery, sourceType: "CostcoCashCard", readOnly });
    const shopCardAmount = formHelper.getFieldCtrlModel(lead, "CostcoShopCardAmount", "decimal", false, { label: "Costco Shop Card", readOnly });
    const paymentMethod = formHelper.getFieldCtrlModel(lead, "CostcoPaymentMethodID", "select", false, { label: "Payment Method", displayField: "DisplayName", source: getPaymentMethodQuery, sourceType: "CostcoPaymentMethod", readOnly });
    const executiveMember = formHelper.getFieldCtrlModel(lead, "ExecutiveMember", "check", false, { label: "Executive Member", readOnly });
    const costcoVisaCard = formHelper.getFieldCtrlModel(lead, "CostcoVisaCard", "check", false, { label: "Costco Visa Card", readOnly });
    const leadGenerator = includeLeadGenerator ? formHelper.getFieldCtrlModel(lead, "LeadGenerator", "text", false, { label: "Lead Generator", readOnly }) : null;

    return (
        <React.Fragment>
            <Row>
                <Col md={4}>
                    <FormCtrl model={warehouse} />
                </Col>
                <Col md={4}>
                    <FormCtrl model={service} />
                </Col>
                <Col md={4}>
                    <FormCtrl model={adCode} />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <FormCtrl model={cashCard} />
                </Col>
                <Col md={4}>
                    <FormCtrl model={shopCardAmount} />
                </Col>
                <Col md={4}>
                    <FormCtrl model={paymentMethod} />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <FormCtrl model={executiveMember} />
                </Col>
                <Col md={4}>
                    <FormCtrl model={costcoVisaCard} />
                </Col>
                {includeLeadGenerator && (
                    <Col md={4}>
                        <FormCtrl model={leadGenerator} />
                    </Col>
                )}
            </Row>
        </React.Fragment>
    );
};

const CostLeadContentCtrl = ({ entity, opportunity, readOnly, setDirty, onRefresh, onChangeBusinessUnit, allowCreate = true }) => {
    const owner = entity ?? opportunity;
    // When the integration is disabled a linked lead is still shown for reference, but
    // nothing can be edited or sent. The server also rejects live actions when disabled.
    const integrationEnabled = isCostcoIntegrationEnabled();
    const effectiveReadOnly = readOnly || !integrationEnabled;
    const [lead, setLead] = React.useState(owner?.CostcoLead ?? null);
    const [syncOptions, setSyncOptions] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [businessUnitMapping, setBusinessUnitMapping] = React.useState(null);
    const [mappingCheckKey, setMappingCheckKey] = React.useState(1);

    React.useEffect(() => {
        let isMounted = true;

        const loadLead = async () => {
            const currentLead = owner?.CostcoLead ?? null;

            if (accentUtils.isNull(owner) || accentUtils.isNull(owner.CostcoLeadID) || !accentUtils.isNull(currentLead)) {
                return currentLead;
            }

            return await loadEntityNavigationPropertyValue(owner, "CostcoLead");
        };

        const loadSyncOptions = async () => {
            if (!integrationEnabled) return null;

            try {
                const result = await update("CostcoIntegration_GetLeadSyncOptions", {});
                return result?.Success ? result : null;
            } catch {
                return null;
            }
        };

        const loadData = async () => {
            setLoading(true);

            try {
                const [loadedLead, loadedOptions] = await Promise.all([loadLead(), loadSyncOptions()]);

                if (isMounted) {
                    setLead(loadedLead);
                    setSyncOptions(loadedOptions);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [owner, integrationEnabled]);

    // Look up the business unit the warehouse/service mapping expects so a
    // mismatch with the opportunity/job business unit can be flagged.
    React.useEffect(() => {
        let isMounted = true;

        const warehouseID = lead?.CostcoWarehouseID;
        const serviceID = lead?.CostcoServiceID;

        if (accentUtils.isEmpty(warehouseID) || accentUtils.isEmpty(serviceID)) {
            setBusinessUnitMapping(null);
            return undefined;
        }

        query_Costco_BusinessUnitMapping.getFirstOrDefault({ warehouseID, serviceID }).then(mapping => {
            if (isMounted) {
                setBusinessUnitMapping(mapping ?? null);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [lead, lead?.CostcoWarehouseID, lead?.CostcoServiceID, mappingCheckKey]);

    // The bound field controls mutate the lead without re-rendering this
    // component, so a warehouse/service change bumps the key to re-evaluate.
    const onWarehouseOrServiceChanged = () => setMappingCheckKey(k => k + 1);

    const getDefaultAdCodeID = async () => {
        const leadSourceID = owner?.PL_LeadSourceID ?? owner?.Opportunity?.PL_LeadSourceID;
        if (accentUtils.isNull(leadSourceID)) {
            return null;
        }

        const matchingAdCodes = await query_CostcoAdCode_SelectList.getAll({ leadSourceID });
        return matchingAdCodes?.length === 1 ? matchingAdCodes[0].ID : null;
    };

    const createLead = async () => {
        const defaultAdCodeID = await getDefaultAdCodeID();
        const newLead = await addEntity("CostcoLead", {
            CostcoAdCodeID: defaultAdCodeID
        });

        setEntityMemberValue(owner, "CostcoLead", newLead);
        setLead(newLead);

        if (!accentUtils.isNull(setDirty)) {
            setDirty();
        }
    };
    const completeLead = async () => {
        const e = await showDialog(<CostcoCompleteLeadDlg lead={lead} />);
        if (e.canceled) return;

        const result = await update("CostcoIntegration_CompleteLead", { CostcoLeadID: lead.ID, ...e.payload }, true);
        if (result?.Success) {
            const loadedLead = await loadEntityNavigationPropertyValue(owner, "CostcoLead");
            setLead(loadedLead);
            await onRefresh?.();

            showOK("Complete Costco Lead", result.Message || "Costco lead completed.", ["application_strings.application.buttons.ok"], false, null, true);
            return;
        }

        showOK("Complete Costco Lead", result?.Message || "Unable to complete Costco lead.", ["application_strings.application.buttons.ok"], false, null, true);
    };

    const sendMilestone = async (action, title, data) => {
        const result = await update(action, { CostcoLeadID: lead.ID, ...data }, true);

        if (result?.Success) {
            const loadedLead = await loadEntityNavigationPropertyValue(owner, "CostcoLead");
            setLead(loadedLead);
            await onRefresh?.();
        }

        showOK(title, result?.Message || `Unable to ${title.toLowerCase()}.`, ["application_strings.application.buttons.ok"], false, null, true);
    };

    const overrideMilestone = async () => {
        const e = await showDialog(<CostcoMilestoneOverrideDlg lead={lead} />);
        if (e.canceled) return;

        await sendMilestone("CostcoIntegration_OverrideMilestone", "Override Milestone", e.payload);
    };

    if (loading) {
        return <Col md={12}><AccentSpinner /></Col>;
    }

    if (accentUtils.isNull(lead)) {
        if (!allowCreate || !integrationEnabled) return null;

        return (
            <Col md={12}>
                <AccentButton disabled={readOnly} onClick={createLead}>Create as Costco lead</AccentButton>
            </Col>
        );
    }

    const centahLeadID = formHelper.getFieldCtrlModel(lead, "CentahLeadID", "text", false, { label: "Costco Lead ID", readOnly: true });
    const leadGenerator = formHelper.getFieldCtrlModel(lead, "LeadGenerator", "text", false, { label: "Lead Generator", readOnly: effectiveReadOnly });
    const statusName = formHelper.getFieldCtrlModel(lead, "StatusName", "text", false, { label: "Status", readOnly: true });
    const bookDate = formHelper.getFieldCtrlModel(lead, "BookDate", "date", false, { label: "Book Date", readOnly: true });
    const isSold = integrationEnabled && `${lead.StatusName ?? lead.Status ?? ""}`.toUpperCase() === "SOLD";
    const hasCentahLead = !accentUtils.isNullOrEmpty(lead.CentahLeadID);
    const ownerStatus = owner?.Status ?? owner?.Opportunity?.Status;
    const isOwnerLost = owner?.IsLost === true
        || ownerStatus === opporunityStatus.Lost
        || ownerStatus === opporunityStatus.Abandoned;
    const manualSendButtons = hasCentahLead && !accentUtils.isNull(syncOptions)
        ? [
            { action: "CostcoIntegration_SendEstimate", title: "Send Estimate Date", autoSync: syncOptions.AutoSyncEstimate, canSend: syncOptions.CanSendEstimate, sentOn: lead.EstimateSentOn },
            { action: "CostcoIntegration_SendQuote", title: "Send Quote", autoSync: syncOptions.AutoSyncQuote, canSend: syncOptions.CanSendQuote, sentOn: lead.QuoteSentOn },
            { action: "CostcoIntegration_SendSold", title: "Send Sale", autoSync: syncOptions.AutoSyncSold, canSend: syncOptions.CanSendSold, sentOn: lead.SoldSentOn },
            { action: "CostcoIntegration_SendCancel", title: "Send Cancel", autoSync: syncOptions.AutoSyncCancel, canSend: syncOptions.CanSendCancel, sentOn: lead.CancelledSentOn, visible: isOwnerLost }
        ].filter(b => b.autoSync === false && b.canSend !== false && accentUtils.isNull(b.sentOn) && b.visible !== false)
        : [];
    const resendButtons = hasCentahLead && syncOptions?.CanResendMilestones === true
        ? [
            { milestone: "Estimate", title: "Resend Estimate Date", sentOn: lead.EstimateSentOn },
            { milestone: "Quote", title: "Resend Quote", sentOn: lead.QuoteSentOn },
            { milestone: "Sold", title: "Resend Sale", sentOn: lead.SoldSentOn },
            { milestone: "Cancel", title: "Resend Cancel", sentOn: lead.CancelledSentOn, visible: isOwnerLost }
        ].filter(b => !accentUtils.isNull(b.sentOn) && b.visible !== false)
        : [];
    const showOverride = hasCentahLead && syncOptions?.CanOverrideMilestones === true;
    const showUploadLead = integrationEnabled && !hasCentahLead && lead.ID > 0 && syncOptions?.CanUploadLead !== false;
    const showAuditHistory = lead.ID > 0 && (getAccess().features().FeatureAllowManagement() || getAccess().features().FeatureAllowSetup());
    const syncFields = [
        ["AcknowledgedOn", "datetime", "Acknowledged On"],
        ["EstimateDateSent", "date", "Estimate Date Sent"],
        ["EstimateSentOn", "datetime", "Estimate Sent On"],
        ["QuoteDateSent", "date", "Quote Date Sent"],
        ["QuoteAmountSent", "decimal", "Quote Amount Sent"],
        ["QuoteSentOn", "datetime", "Quote Sent On"],
        ["SoldDateSent", "date", "Sold Date Sent"],
        ["SoldAmountSent", "decimal", "Sold Amount Sent"],
        ["SoldSentOn", "datetime", "Sold Sent On"],
        ["WorkEndDateSent", "date", "Work End Date Sent"],
        ["WorkCompletionSentOn", "datetime", "Work Completion Sent On"],
        ["CentahCancelReasonID", "number", "Centah Cancel Reason ID"],
        ["CancelledSentOn", "datetime", "Cancelled Sent On"]
    ].map(([fieldName, fieldType, label]) =>
        formHelper.getFieldCtrlModel(lead, fieldName, fieldType, false, { label, readOnly: true })
    );

    const ownerBusinessUnitID = owner?.BusinessUnitID ?? null;
    const businessUnitMismatch = integrationEnabled
        && !accentUtils.isNull(businessUnitMapping)
        && !accentUtils.isNull(businessUnitMapping.BusinessUnitID)
        && businessUnitMapping.BusinessUnitID !== ownerBusinessUnitID;
    const allowChangeBusinessUnit = !accentUtils.isNull(onChangeBusinessUnit)
        && getAccess().features().FeatureAllowChangeBusinessUnit()
        && getAccess().modules().RTL_BusinessUnit();

    return (
        <Col md={12}>
            {!integrationEnabled && (
                <Row>
                    <Col md={12}>
                        <Alert color="secondary" className="mb-2">
                            Costco integration is disabled. Lead details are shown for reference only.
                        </Alert>
                    </Col>
                </Row>
            )}
            {businessUnitMismatch && (
                <Row>
                    <Col md={12}>
                        <Alert color="warning" className="mb-2 d-flex align-items-center justify-content-between">
                            <span>
                                {`The Costco warehouse and service mapping expects business unit "${businessUnitMapping.BusinessUnitName ?? ""}", but this ${accentUtils.isNull(entity) ? "opportunity" : "job"} is assigned to a different business unit.`}
                            </span>
                            {allowChangeBusinessUnit && (
                                <AccentButton tagName="fixCostcoBusinessUnit" onClick={() => onChangeBusinessUnit([businessUnitMapping.BusinessUnitID])}>Change Business Unit</AccentButton>
                            )}
                        </Alert>
                    </Col>
                </Row>
            )}
            {(isSold || manualSendButtons.length > 0 || resendButtons.length > 0 || showOverride || showUploadLead || showAuditHistory) && (
                <Row>
                    <Col md={12} className="mb-2">
                        {showUploadLead && (
                            <AccentButton onClick={() => sendMilestone("CostcoIntegration_UploadLead", "Upload Lead")}>Upload Lead</AccentButton>
                        )}
                        {isSold && <AccentButton onClick={completeLead}>Complete Lead</AccentButton>}
                        {manualSendButtons.map(button => (
                            <AccentButton key={button.action} onClick={() => sendMilestone(button.action, button.title)}>{button.title}</AccentButton>
                        ))}
                        {resendButtons.map(button => (
                            <AccentButton key={`resend_${button.milestone}`} onClick={() => sendMilestone("CostcoIntegration_ResendMilestone", button.title, { Milestone: button.milestone })}>{button.title}</AccentButton>
                        ))}
                        {showOverride && (
                            <AccentButton onClick={overrideMilestone}>Override Milestone</AccentButton>
                        )}
                        {showAuditHistory && (
                            <AccentButton onClick={() => showAuditLog({ entityType: "CostcoLead", id: lead.ID })}>Audit History</AccentButton>
                        )}
                    </Col>
                </Row>
            )}
            <CostcoLeadFieldsCtrl lead={lead} readOnly={effectiveReadOnly} onChange={onWarehouseOrServiceChanged} />
            <Row>
                <Col md={4}>
                    <AccentFormCtrl model={centahLeadID} />
                    <AccentFormCtrl model={leadGenerator} />
                </Col>
                <Col md={4}>
                    <AccentFormCtrl model={statusName} />
                </Col>
                <Col md={4}>
                    <AccentFormCtrl model={bookDate} />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <h5 className="mt-3">Centah Sync Status</h5>
                </Col>
            </Row>
            {[0, 3, 6, 9, 12].map(start => (
                <Row key={start}>
                    {syncFields.slice(start, start + 3).map(field => (
                        <Col md={4} key={field.field.fieldName}>
                            <AccentFormCtrl model={field} />
                        </Col>
                    ))}
                </Row>
            ))}
        </Col>
    );
};

const overrideMilestoneOptions = [
    { ID: "Estimate", DisplayName: "Estimate Date" },
    { ID: "Quote", DisplayName: "Quote" },
    { ID: "Sold", DisplayName: "Sale" },
    { ID: "Cancel", DisplayName: "Cancel" }
];

const CostcoMilestoneOverrideDlg = ({ lead, onClose }) => {
    const [milestone, setMilestone] = React.useState(null);
    const [cancelReasons, setCancelReasons] = React.useState([]);
    const [membershipNumber, setMembershipNumber] = React.useState(null);
    const values = React.useRef({});

    React.useEffect(() => {
        query_CostcoCancelReason_SelectList.getAll().then(r => setCancelReasons(r || []));

        // Pre-fill from the contact's membership number (same source the server
        // falls back to when the override field is left empty) so the user can
        // see and correct what will actually be sent.
        if (!accentUtils.isNull(lead?.ID)) {
            query_CostcoLead_ContactMembershipNumber.getFirstOrDefault({ id: lead.ID }).then(r => {
                const number = r?.MembershipNumber ?? null;
                if (accentUtils.isNull(values.current.MembershipNumber)) {
                    values.current.MembershipNumber = number;
                }
                setMembershipNumber(number);
            });
        }
    }, []);

    const setValue = (name, value) => {
        values.current[name] = value;
    };

    const getValidationError = () => {
        const v = values.current;

        if (milestone === "Estimate" && accentUtils.isNull(v.EstimateDate)) return "An estimate date is required.";
        if (milestone === "Quote" && (accentUtils.isNull(v.QuoteDate) || accentUtils.isNull(v.QuoteAmount))) return "A quote date and quote amount are required.";
        if (milestone === "Sold" && (accentUtils.isNull(v.SoldDate) || accentUtils.isNull(v.SoldAmount) || accentUtils.isNull(v.WorkStartDate) || accentUtils.isNull(v.WorkEndDate))) return "A sold date, sold amount, work start date and work end date are required.";
        if (milestone === "Cancel" && accentUtils.isNull(v.CostcoCancelReasonID)) return "A cancel reason is required.";

        return null;
    };

    const doSend = (e) => {
        const validationError = getValidationError();
        if (validationError) {
            showOK("Override Milestone", validationError, ["application_strings.application.buttons.ok"], false, null, true);
            e.promise();
            return;
        }

        onClose({ payload: { Milestone: milestone, ...values.current } });
        e.promise();
    };

    return (
        <AccentDialog
            headerContent="Override Milestone"
            onClose={onClose}
            maxWidth={700}
            actionsContent={[
                <AccentButton key="send" disabled={accentUtils.isNull(milestone)} onClick={doSend}>Send to Centah</AccentButton>
            ]}
        >
            <Row>
                <Col md={12} className="mb-2">
                    <label>Sends the entered values to Centah as-is, replacing the calculated values. Use with care.</label>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <AccentComboUnbound
                        label="Milestone"
                        data={overrideMilestoneOptions}
                        valueField="ID"
                        displayField="DisplayName"
                        onChange={v => { values.current = v === "Sold" ? { MembershipNumber: membershipNumber } : {}; setMilestone(v); }} />
                </Col>
            </Row>
            {milestone === "Estimate" && (
                <Row>
                    <Col md={6}>
                        <AccentDateUnbound label="Estimate Date" onChange={v => setValue("EstimateDate", v)} />
                    </Col>
                </Row>
            )}
            {milestone === "Quote" && (
                <Row>
                    <Col md={4}>
                        <AccentDateUnbound label="Quote Date" onChange={v => setValue("QuoteDate", v)} />
                    </Col>
                    <Col md={4}>
                        <AccentNumberUnbound label="Quote Amount (ex tax)" onChange={v => setValue("QuoteAmount", v)} />
                    </Col>
                    <Col md={4}>
                        <AccentNumberUnbound label="Sales Tax" onChange={v => setValue("QuoteSalesTax", v)} />
                    </Col>
                </Row>
            )}
            {milestone === "Sold" && (
                <>
                    <Row>
                        <Col md={4}>
                            <AccentDateUnbound label="Sold Date" onChange={v => setValue("SoldDate", v)} />
                        </Col>
                        <Col md={4}>
                            <AccentNumberUnbound label="Sold Amount (ex tax)" onChange={v => setValue("SoldAmount", v)} />
                        </Col>
                        <Col md={4}>
                            <AccentNumberUnbound label="Sales Tax" onChange={v => setValue("SoldSalesTax", v)} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <AccentDateUnbound label="Work Start Date" onChange={v => setValue("WorkStartDate", v)} />
                        </Col>
                        <Col md={4}>
                            <AccentDateUnbound label="Work End Date" onChange={v => setValue("WorkEndDate", v)} />
                        </Col>
                        <Col md={4}>
                            <AccentTextUnbound key={membershipNumber ?? "mn"} label="Membership Number (optional)" defaultValue={membershipNumber} onChange={v => setValue("MembershipNumber", v)} />
                        </Col>
                    </Row>
                </>
            )}
            {milestone === "Cancel" && (
                <Row>
                    <Col md={6}>
                        <AccentComboUnbound
                            label="Cancel Reason"
                            data={cancelReasons}
                            valueField="ID"
                            displayField="DisplayName"
                            onChange={v => setValue("CostcoCancelReasonID", v)} />
                    </Col>
                    <Col md={6}>
                        <AccentTextUnbound label="Cancel Details (optional)" onChange={v => setValue("CancelDetails", v)} />
                    </Col>
                </Row>
            )}
        </AccentDialog>
    );
};

export const CostLeadCtrl = ({ entity, opportunity, readOnly, setDirty, onRefresh, onChangeBusinessUnit, allowCreate = true }) => {
    return (
        <AccentSectionHeader title="Costco Lead" collapsed={true} lazyLoad>
            <CostLeadContentCtrl entity={entity} opportunity={opportunity} readOnly={readOnly} setDirty={setDirty} onRefresh={onRefresh} onChangeBusinessUnit={onChangeBusinessUnit} allowCreate={allowCreate} />
        </AccentSectionHeader>
    );
};
